import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Storefront as StorefrontIcon,
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Handshake as HandshakeIcon,
  Verified as VerifiedIcon,
  AccountTree as AccountTreeIcon,
  CardGiftcard as GiftCardIcon,
  SupportAgent as SupportIcon,
  Build as BuildIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

/* ─── palette ─── */
const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#8f8f8f';
const MONO = '"JetBrains Mono", monospace';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
  },
});

/* ─── shared section wrapper ─── */
function Section({
  children,
  id,
  sx,
}: {
  children: React.ReactNode;
  id?: string;
  sx?: object;
}) {
  return (
    <Box component="section" id={id} sx={{ py: { xs: 8, md: 12 }, ...sx }}>
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

/* ─── reusable card ─── */
function InfoCard({
  icon,
  title,
  body,
}: {
  icon?: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Box
      sx={{
        p: 3.5,
        borderRadius: 3,
        bgcolor: BG_CARD,
        border: '1px solid rgba(255,255,255,0.06)',
        height: '100%',
      }}
    >
      {icon && (
        <Box sx={{ color: ELECTRIC_BLUE, mb: 1.5 }}>{icon}</Box>
      )}
      <Typography
        sx={{ color: '#fff', fontWeight: 700, fontSize: '1.12rem', mb: 1.25 }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: TEXT_DIM, lineHeight: 1.75 }}>{body}</Typography>
    </Box>
  );
}

/* ─── section headline helper ─── */
function SectionHeadline({
  chip,
  chipColor,
  title,
  subtitle,
}: {
  chip?: string;
  chipColor?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <>
      {chip && (
        <Chip
          label={chip}
          sx={{
            mb: 2,
            bgcolor: `${chipColor || ELECTRIC_BLUE}18`,
            color: chipColor || ELECTRIC_BLUE,
            border: `1px solid ${chipColor || ELECTRIC_BLUE}50`,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: 0.5,
          }}
        />
      )}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: '1.9rem', md: '2.55rem' },
          mb: subtitle ? 2 : 4,
          lineHeight: 1.15,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            color: TEXT_DIM,
            fontSize: { xs: '1.05rem', md: '1.18rem' },
            lineHeight: 1.7,
            maxWidth: 780,
            mb: 4,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );
}

/* ────────────────────── PAGE ────────────────────── */

export default function ForPartnersPage() {
  const router = useRouter();
  const [formSubmitted, setFormSubmitted] = useState(false);

  /* ── form submission ── */
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      await fetch('https://formspree.io/f/mzdadlgw', {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });
      setFormSubmitted(true);
    } catch {
      // allow the default form action to handle it
      form.submit();
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>Aithon — Partner With Us | Compete With Value, Not Politics</title>
        <meta
          name="description"
          content="Launch a branded B2B services storefront on Aithon. Attach Perks. Compete for the buy box. Earn recurring commissions on closed deals. No pay-per-click tax."
        />
        <link rel="canonical" href="https://aithon.tech/for-partners" />
        <meta
          property="og:title"
          content="Aithon — Partner With Us | Compete With Value, Not Politics"
        />
        <meta
          property="og:description"
          content="Launch a branded storefront, attach Perks, compete for featured placement, and earn recurring commission — without opaque quoting or back-channel games."
        />
      </Head>

      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        {/* ════════════════════ 1 · HERO ════════════════════ */}
        <Box
          sx={{
            pt: { xs: 12, md: 18 },
            pb: { xs: 8, md: 14 },
            background:
              'radial-gradient(ellipse at 25% 20%, rgba(33,150,243,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(255,152,0,0.06) 0%, transparent 50%)',
          }}
        >
          <Container maxWidth="lg">
            <Chip
              label="FOR PARTNERS"
              sx={{
                mb: 3,
                bgcolor: 'rgba(255,152,0,0.1)',
                color: ORANGE,
                border: '1px solid rgba(255,152,0,0.3)',
                fontWeight: 700,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4.2rem' },
                lineHeight: 1.08,
                mb: 3,
                maxWidth: 900,
              }}
            >
              Turn your expertise into a storefront that wins on value.
            </Typography>
            <Typography
              sx={{
                color: TEXT_DIM,
                fontSize: { xs: '1.08rem', md: '1.28rem' },
                lineHeight: 1.7,
                maxWidth: 880,
                mb: 5,
              }}
            >
              Aithon helps consultants, MSPs, resellers, and service firms list
              real telecom, cloud, cybersecurity, and IT services — attach Perks,
              compete for featured placement, and earn recurring commissions on
              closed deals. No pay-per-click tax. No back-channel politics.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => router.push('/registerpartner')}
                sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
              >
                Register as a Partner
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  document
                    .getElementById('economics')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  color: TEXT,
                  borderColor: 'rgba(255,255,255,0.2)',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                See Partner Economics
              </Button>
            </Stack>

            {/* micro-proof row */}
            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={3}
              sx={{ mt: 5, gap: 1.5 }}
            >
              {[
                'Branded storefront',
                'Perks-driven differentiation',
                'Transaction + payout infrastructure',
                'Early-partner advantage',
              ].map((t) => (
                <Stack key={t} direction="row" spacing={0.75} alignItems="center">
                  <CheckIcon sx={{ fontSize: 16, color: GREEN }} />
                  <Typography sx={{ color: TEXT_DIM, fontSize: '0.88rem' }}>
                    {t}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ════════════════════ 2 · WHAT AITHON IS ════════════════════ */}
        <Section id="what-aithon-is">
          <SectionHeadline
            chip="THE PLATFORM"
            chipColor={ELECTRIC_BLUE}
            title="The commerce layer for B2B services."
            subtitle="Aithon is not a directory, not a storefront builder, not a referral network. It is a partner commerce platform for telecom, cloud, cybersecurity, and IT services."
          />
          <Grid container spacing={3}>
            {[
              {
                icon: <BuildIcon />,
                title: 'Providers',
                body: 'Supply the underlying services — connectivity, cloud, security, managed IT. Set commission structures and let partners compete to sell them.',
              },
              {
                icon: <StorefrontIcon />,
                title: 'Partners',
                body: 'Launch branded storefronts. List services, attach Perks, and compete for featured placement through value — not politics.',
              },
              {
                icon: <HandshakeIcon />,
                title: 'Buyers',
                body: 'Compare transparent offers side-by-side. See real pricing, real Perks, real trust signals. Request quotes or transact directly.',
              },
              {
                icon: <AccountTreeIcon />,
                title: 'Aithon',
                body: 'Powers storefronts, rankings, transaction workflows, and commission payouts. The commerce infrastructure that makes the whole ecosystem work.',
              },
            ].map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.title}>
                <InfoCard icon={card.icon} title={card.title} body={card.body} />
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ════════════════════ 3 · PERKS ARE THE ENGINE ════════════════════ */}
        <Section sx={{ bgcolor: BG_SURFACE }} id="perks">
          <SectionHeadline
            chip="THE DIFFERENTIATOR"
            chipColor={ORANGE}
            title="Perks are the engine."
            subtitle="The best partner shouldn't win because they answered the phone first. They should win because they created the best total offer."
          />
          <Typography
            sx={{
              color: TEXT_DIM,
              lineHeight: 1.8,
              maxWidth: 780,
              mb: 5,
            }}
          >
            A <strong style={{ color: TEXT }}>Perk</strong> is a value-add
            attached to a service listing. Multiple partners can sell the same
            underlying service — Perks are how they differentiate. Instead of
            hiding behind opaque quoting, partners compete with visible,
            meaningful value.
          </Typography>
          <Grid container spacing={2.5}>
            {[
              {
                icon: <SecurityIcon />,
                title: 'Free Security Audit',
                body: 'Demonstrate expertise by offering a complimentary assessment with every connectivity deal.',
              },
              {
                icon: <SupportIcon />,
                title: 'White-Glove Onboarding',
                body: 'Hands-on setup and training that turns a commodity purchase into a high-touch experience.',
              },
              {
                icon: <GiftCardIcon />,
                title: 'Bill Credit or Rebate',
                body: 'Put money back in the buyer\'s pocket — funded from commission, not margin.',
              },
              {
                icon: <BuildIcon />,
                title: 'Hardware Bundle',
                body: 'Include a router, firewall, or access point as part of the deal to remove friction.',
              },
              {
                icon: <SpeedIcon />,
                title: 'Migration Assistance',
                body: 'Handle the hardest part of switching providers — reducing the buyer\'s risk to nearly zero.',
              },
              {
                icon: <VerifiedIcon />,
                title: 'Rollback Guarantee',
                body: 'Promise a clean exit if the service doesn\'t deliver. Confidence is a competitive advantage.',
              },
            ].map((perk) => (
              <Grid item xs={12} sm={6} md={4} key={perk.title}>
                <InfoCard
                  icon={perk.icon}
                  title={perk.title}
                  body={perk.body}
                />
              </Grid>
            ))}
          </Grid>

          {/* before / after strip */}
          <Grid container spacing={3} sx={{ mt: 6 }}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: TEXT_DIM,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Without Aithon
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Same service, same vague promise',
                    'Hidden differentiators',
                    'Buyer can\'t compare value',
                    'Whoever has the relationship wins',
                  ].map((t) => (
                    <Typography key={t} sx={{ color: TEXT_DIM, lineHeight: 1.7 }}>
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: `1px solid ${ELECTRIC_BLUE}30`,
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: ELECTRIC_BLUE,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  With Aithon + Perks
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Same service, visible Perks',
                    'Transparent value competition',
                    'Buyer compares real offers',
                    'Best value wins featured placement',
                  ].map((t) => (
                    <Typography key={t} sx={{ color: TEXT, lineHeight: 1.7 }}>
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ════════════════════ 4 · HOW THE BUY BOX WORKS ════════════════════ */}
        <Section id="buy-box">
          <SectionHeadline
            chip="THE MECHANISM"
            chipColor={GREEN}
            title="How the buy box works."
            subtitle="Featured placement is earned through value and trust — not purchased. Better offers win visibility."
          />
          <Grid container spacing={2}>
            {[
              {
                step: '01',
                title: 'List your services',
                body: 'Add real telecom, cloud, cybersecurity, or IT services to your branded storefront with transparent pricing.',
              },
              {
                step: '02',
                title: 'Attach Perks',
                body: 'Add value-adds that make your offer stronger — security audits, onboarding support, bill credits, hardware bundles.',
              },
              {
                step: '03',
                title: 'Buyers compare offers',
                body: 'Multiple partners may list the same service. Buyers see side-by-side comparisons with visible Perks and pricing.',
              },
              {
                step: '04',
                title: 'Value + trust determine placement',
                body: 'Aithon evaluates offers using Perk Value Score and Trust Score — derived from real transaction data, not opinions.',
              },
              {
                step: '05',
                title: 'Transact and earn',
                body: 'Deals close inside the platform. Commission is tracked, renewals compound, and your Trust Score grows with every successful delivery.',
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={2.4} key={item.step}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: BG_CARD,
                    border: '1px solid rgba(255,255,255,0.06)',
                    height: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      color: ELECTRIC_BLUE,
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      mb: 1.5,
                    }}
                  >
                    {item.step}
                  </Typography>
                  <Typography
                    sx={{ color: '#fff', fontWeight: 700, mb: 1 }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}>
                    {item.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box
            sx={{
              mt: 4,
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'rgba(33,150,243,0.06)',
              border: '1px solid rgba(33,150,243,0.15)',
              maxWidth: 680,
            }}
          >
            <Typography sx={{ color: TEXT, fontStyle: 'italic', lineHeight: 1.7 }}>
              &ldquo;We don&rsquo;t sell placement. We don&rsquo;t sell trust.
              The algorithm is the algorithm. Money buys verification and
              convenience — never rank.&rdquo;
            </Typography>
          </Box>
        </Section>

        {/* ════════════════════ 5 · WHO THIS IS FOR ════════════════════ */}
        <Section sx={{ bgcolor: BG_SURFACE }} id="who">
          <SectionHeadline
            title="Built for the partners who actually sell."
            subtitle="Whether you're a solo consultant, a managed services firm, or a service provider — there's a lane for you."
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<TrendingUpIcon />}
                title="Consultants, Telecom Agents & Resellers"
                body="You already sell carrier and IT services. Aithon gives you a branded storefront, transparent pricing, and Perks-based differentiation — so you win on value instead of relationship luck and opaque quoting."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<SecurityIcon />}
                title="MSPs & Service Firms"
                body="Use commodity telecom and cloud demand as a wedge. Attach your own service — security audits, managed IT, onboarding — as a Perk. Win the listing, earn the commission, and open the door to higher-margin recurring work."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard
                icon={<StorefrontIcon />}
                title="Providers & Suppliers"
                body="Let partners compete to sell your services through value, visibility, and performance. Structured merchandising. Transparent competition. Partners earn commission; you earn distribution without channel conflict."
              />
            </Grid>
          </Grid>
        </Section>

        {/* ════════════════════ 6 · THE OPENING ACT MODEL ════════════════════ */}
        <Section id="opening-act">
          <SectionHeadline
            chip="THE STRATEGY"
            chipColor={ORANGE}
            title="Let the circuit open the door. Let your service close the relationship."
            subtitle="Use commodity demand as customer acquisition. Use Perks to land higher-value recurring business."
          />
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.5}>
                {[
                  {
                    num: '1',
                    text: 'A buyer searches for business internet.',
                  },
                  {
                    num: '2',
                    text: 'Your listing includes a free security assessment as a Perk.',
                  },
                  {
                    num: '3',
                    text: 'The buyer chooses your offer — the Perk tipped the decision.',
                  },
                  {
                    num: '4',
                    text: 'You earn commission on the connectivity deal.',
                  },
                  {
                    num: '5',
                    text: 'The security assessment opens the door to a recurring managed services engagement worth 5× the circuit.',
                  },
                ].map((item) => (
                  <Stack
                    key={item.num}
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,152,0,0.14)',
                        color: ORANGE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                      }}
                    >
                      {item.num}
                    </Box>
                    <Typography sx={{ color: TEXT, lineHeight: 1.7, pt: 0.3 }}>
                      {item.text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,152,0,0.2)',
                }}
              >
                <Typography
                  sx={{ color: ORANGE, fontWeight: 700, mb: 1.5 }}
                >
                  Why this matters
                </Typography>
                <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8 }}>
                  Perks aren&rsquo;t just incentives. They&rsquo;re{' '}
                  <strong style={{ color: TEXT }}>
                    customer acquisition architecture
                  </strong>
                  . A $300 security audit, funded from commission, can open an
                  account worth $24,000/year in managed services. That&rsquo;s
                  not a discount. That&rsquo;s a business strategy.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ════════════════════ 7 · PARTNER ECONOMICS ════════════════════ */}
        <Section sx={{ bgcolor: BG_SURFACE }} id="economics">
          <SectionHeadline
            chip="THE ECONOMICS"
            chipColor={GREEN}
            title="How partners make money."
            subtitle="Pay-per-closed-sale. No monthly fees. No media spend. Commission compounds with every renewal."
          />
          <Grid container spacing={4}>
            {/* scenario 1 */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    color: ORANGE,
                    fontSize: '0.78rem',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Telecom Consultant
                </Typography>
                <Typography
                  sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}
                >
                  $10,000/mo connectivity deal
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Wins featured listing with a relevant Perk',
                    '15% residual commission = $1,500/mo',
                    'Platform success fee deducted from proceeds',
                    'Over 3 years: $48,600+ in recurring revenue',
                    'Every renewal triggers new competition',
                  ].map((t) => (
                    <Typography
                      key={t}
                      sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}
                    >
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
            {/* scenario 2 */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    color: ORANGE,
                    fontSize: '0.78rem',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  MSP — Perk Strategy
                </Typography>
                <Typography
                  sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}
                >
                  Free audit → managed services upsell
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Offers free security audit as Perk (~$300 cost)',
                    'Wins the connectivity listing',
                    'Earns commission on the telecom deal',
                    'Audit converts into $2,000/mo managed services',
                    'Customer LTV: $72,000+ over 3 years',
                  ].map((t) => (
                    <Typography
                      key={t}
                      sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}
                    >
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
            {/* scenario 3 */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO,
                    color: ORANGE,
                    fontSize: '0.78rem',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    mb: 2,
                  }}
                >
                  Lower-Ticket Service
                </Typography>
                <Typography
                  sx={{ color: '#fff', fontWeight: 700, mb: 1.5 }}
                >
                  Smaller deal, still structured
                </Typography>
                <Stack spacing={1}>
                  {[
                    'Smaller commission pool',
                    'Lighter Perk requirement',
                    'Still benefits from transparent comparison',
                    'Trust Score accumulates with every delivery',
                    'Volume compounds over time',
                  ].map((t) => (
                    <Typography
                      key={t}
                      sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}
                    >
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* entry cost strip */}
          <Box
            sx={{
              mt: 6,
              p: 3.5,
              borderRadius: 3,
              bgcolor: BG_CARD,
              border: '1px solid rgba(0,230,118,0.15)',
              maxWidth: 700,
            }}
          >
            <Typography sx={{ color: GREEN, fontWeight: 700, mb: 1 }}>
              What it costs to get started
            </Typography>
            <Stack spacing={0.75}>
              <Typography sx={{ color: TEXT, lineHeight: 1.7 }}>
                <strong>$50 one-time setup</strong> — credited to your account
                as platform dollars. You get it back on your first sale.
              </Typography>
              <Typography sx={{ color: TEXT, lineHeight: 1.7 }}>
                <strong>5% success fee</strong> — deducted from deal proceeds
                when deals close. You pay nothing upfront beyond setup.
              </Typography>
              <Typography sx={{ color: TEXT, lineHeight: 1.7 }}>
                <strong>No monthly fees. No media spend. No pay-per-click
                tax.</strong>
              </Typography>
            </Stack>
          </Box>
        </Section>

        {/* ════════════════════ 8 · WHY EARLY PARTNERS WIN ════════════════════ */}
        <Section id="early-advantage">
          <SectionHeadline
            title="Early partners build advantage that compounds."
            subtitle="This isn't just early access. It's structural positioning before the rankings, reputation, and renewals harden."
          />
          <Grid container spacing={3}>
            {[
              {
                icon: <TrophyIcon />,
                title: 'Early Trust Score',
                body: 'Start accumulating transaction-based reputation now — before the market gets crowded. Trust Score is derived from real deliveries, not reviews.',
              },
              {
                icon: <TrendingUpIcon />,
                title: 'Listing Density',
                body: 'Establish listings across key service categories while there\'s less competition. Early density = more visibility when buyer traffic scales.',
              },
              {
                icon: <AnalyticsIcon />,
                title: 'Renewal Compounding',
                body: 'Every deal generates recurring commissions. Start now and your renewal base compounds while others are still onboarding.',
              },
              {
                icon: <StorefrontIcon />,
                title: 'Category Ownership',
                body: 'Be the first serious partner in your market and category. Early entrants set the bar — late entrants fight uphill against established reputations.',
              },
            ].map((card) => (
              <Grid item xs={12} sm={6} key={card.title}>
                <InfoCard
                  icon={card.icon}
                  title={card.title}
                  body={card.body}
                />
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ════════════════════ 9 · WHAT PARTNERS GET ON DAY ONE ════════════════════ */}
        <Section sx={{ bgcolor: BG_SURFACE }} id="day-one">
          <SectionHeadline
            chip="DAY ONE"
            chipColor={ELECTRIC_BLUE}
            title="What you get when you register."
            subtitle="This isn't a waitlist. Register and you're in. Here's what's ready on day one."
          />
          <Grid container spacing={2}>
            {[
              {
                icon: <StorefrontIcon sx={{ fontSize: 24 }} />,
                text: 'Branded storefront — your brand, your catalog, your customer relationships',
              },
              {
                icon: <BuildIcon sx={{ fontSize: 24 }} />,
                text: 'Service listing tools — add real services with transparent pricing',
              },
              {
                icon: <GiftCardIcon sx={{ fontSize: 24 }} />,
                text: 'Perk creation tools — build offers that differentiate you',
              },
              {
                icon: <HandshakeIcon sx={{ fontSize: 24 }} />,
                text: 'Quote and transaction workflow — from inquiry to closed deal',
              },
              {
                icon: <AnalyticsIcon sx={{ fontSize: 24 }} />,
                text: 'Commission visibility and tracking — see what you earn and when',
              },
              {
                icon: <DashboardIcon sx={{ fontSize: 24 }} />,
                text: 'Partner admin dashboard — manage everything in one place',
              },
              {
                icon: <AccountTreeIcon sx={{ fontSize: 24 }} />,
                text: 'AI-readable discovery surface — machine-readable storefront for AI agent traffic',
              },
              {
                icon: <SupportIcon sx={{ fontSize: 24 }} />,
                text: 'Customer workflow infrastructure — post-sale communication and support',
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item.text}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: BG_CARD,
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <Box sx={{ color: ELECTRIC_BLUE }}>{item.icon}</Box>
                  <Typography sx={{ color: TEXT, lineHeight: 1.6 }}>
                    {item.text}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ════════════════════ 10 · PROOF / CREDIBILITY ════════════════════ */}
        <Section id="credibility">
          <SectionHeadline
            title="Built by operators, not theorists."
            subtitle="Aithon is in early access. Here's what's real and what's coming."
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{ color: ORANGE, fontWeight: 700, mb: 1.5 }}
                >
                  Founder credibility
                </Typography>
                <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8 }}>
                  Built by an operator with 20+ years in telecom and Big Tech.
                  Experience includes scaling an Amazon organization from $1B to
                  $3.2B, helping drive multiple tech turnarounds, and seeing a
                  scrappy telecom company through to IPO. Aithon exists because
                  this market is broken and the people who understand it best
                  rarely build the fix.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{ color: ELECTRIC_BLUE, fontWeight: 700, mb: 1.5 }}
                >
                  What&rsquo;s live now
                </Typography>
                <Stack spacing={0.75}>
                  {[
                    'Partner registration and storefront provisioning',
                    'Service listing with transparent pricing',
                    'Perk creation and buy box mechanics',
                    'Transaction and quote workflows',
                    'Commission tracking and payout infrastructure',
                    'AI-readable discovery (agents.json, llms.txt, MCP)',
                  ].map((t) => (
                    <Typography
                      key={t}
                      sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}
                    >
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3.5,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.06)',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{ color: GREEN, fontWeight: 700, mb: 1.5 }}
                >
                  Built for
                </Typography>
                <Stack spacing={0.75}>
                  {[
                    'Business Internet & Connectivity',
                    'Cloud & Infrastructure',
                    'Cybersecurity',
                    'Managed IT Services',
                    'UCaaS & Voice',
                    'SD-WAN & Networking',
                  ].map((t) => (
                    <Typography
                      key={t}
                      sx={{ color: TEXT_DIM, lineHeight: 1.7, fontSize: '0.92rem' }}
                    >
                      • {t}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ════════════════════ 11 · FAQ ════════════════════ */}
        <Section sx={{ bgcolor: BG_SURFACE }} id="faq">
          <SectionHeadline
            title="Frequently asked questions."
            subtitle="Straight answers to the questions serious partners ask before committing."
          />
          {[
            {
              q: 'Do I keep my brand?',
              a: 'Yes. Your storefront is your brand, your domain, your catalog. Aithon is the infrastructure underneath — not the brand on top.',
            },
            {
              q: 'Do I keep the customer relationship?',
              a: 'Yes. You own the customer relationship. Aithon facilitates the transaction layer and commission tracking. The customer is yours.',
            },
            {
              q: 'How do Perks work?',
              a: 'A Perk is a value-add you attach to a service listing — like a free security audit, bill credit, or white-glove onboarding. Perks make your offer stronger and help you compete for featured placement. You decide what to offer and Aithon tracks delivery.',
            },
            {
              q: 'Can multiple partners sell the same service?',
              a: 'Yes. That\'s the point. Multiple partners can list the same underlying service. Perks and Trust Score determine who wins featured placement — not politics or who got there first.',
            },
            {
              q: 'How is featured placement decided?',
              a: 'Featured placement is determined by a combination of Perk Value Score and Trust Score. Trust Score is derived entirely from transaction data — delivery success, SLA compliance, repeat buyers, buyer diversity. Placement is never for sale.',
            },
            {
              q: 'How do commissions and payouts work?',
              a: 'Providers set commission structures on their services. When a deal closes, commission is tracked inside the platform. Aithon deducts the success fee from proceeds and pays out the rest. Recurring commissions compound with every renewal.',
            },
            {
              q: 'Can I attach my own services as Perks?',
              a: 'Yes. This is the opening-act strategy. Attach your managed services, security assessments, or onboarding packages as Perks on commodity listings. Win the deal, earn the commission, and open the door to a higher-value relationship.',
            },
            {
              q: 'Is pricing public?',
              a: 'Yes. Transparent pricing is a core principle. Buyers see real prices and real Perks — no hidden quotes, no bait-and-switch. This is how trust gets built.',
            },
            {
              q: 'What happens after I register?',
              a: 'You get a branded storefront immediately. Add services, create Perks, and start building Trust Score. The $50 setup fee is credited to your account as platform dollars — you get it back on your first sale.',
            },
            {
              q: 'Is Aithon live?',
              a: 'Aithon is in early access. Partner registration, storefronts, service listings, Perks, buy box mechanics, and transaction workflows are live. Early partners are building structural advantage while the market is still forming.',
            },
          ].map((faq) => (
            <Accordion
              key={faq.q}
              sx={{
                bgcolor: BG_CARD,
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '12px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: TEXT_DIM }} />}
                sx={{ px: 3, py: 1 }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                <Typography sx={{ color: TEXT_DIM, lineHeight: 1.8 }}>
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Section>

        {/* ════════════════════ 12 · FINAL CTA ════════════════════ */}
        <Section id="get-started">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <SectionHeadline
                title="Ready to compete with value?"
                subtitle="If you already sell telecom, cloud, cybersecurity, or IT services — Aithon gives you a better way to win. If you have your own service business, Aithon gives you a new acquisition engine."
              />
              <Stack spacing={2} sx={{ mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => router.push('/registerpartner')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    maxWidth: 320,
                  }}
                >
                  Register as a Partner
                </Button>
              </Stack>
              <Typography
                sx={{
                  color: TEXT_DIM,
                  lineHeight: 1.8,
                  maxWidth: 500,
                  fontSize: '0.95rem',
                }}
              >
                Aithon gives partners a branded storefront, a value-based way to
                compete, and the infrastructure to turn closed deals into
                recurring economics. If you understand Perks, you understand
                where this market is going.
              </Typography>
            </Grid>

            {/* ── Talk to Us form (Formspree → Calendly 2-step) ── */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  borderRadius: 3,
                  bgcolor: BG_CARD,
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {!formSubmitted ? (
                  <>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        mb: 0.75,
                      }}
                    >
                      Want to learn more? Let&rsquo;s talk.
                    </Typography>
                    <Typography
                      sx={{ color: TEXT_DIM, mb: 3, lineHeight: 1.7 }}
                    >
                      Tell us a bit about your business — then book a call to
                      walk through the platform live.
                    </Typography>
                    <Box
                      component="form"
                      onSubmit={handleFormSubmit}
                      action="https://formspree.io/f/mzdadlgw"
                      method="POST"
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                    >
                      <input
                        type="hidden"
                        name="_subject"
                        value="🤝 Aithon Partner Inquiry — aithon.tech/for-partners"
                      />
                      <input
                        type="hidden"
                        name="form_type"
                        value="partner_inquiry"
                      />
                      <TextField
                        name="name"
                        label="Your name"
                        placeholder="Jane Smith"
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                          },
                        }}
                      />
                      <TextField
                        name="company"
                        label="Company + what you do"
                        placeholder="e.g. Acme Networks — telecom consulting"
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                          },
                        }}
                      />
                      <TextField
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="you@company.com"
                        required
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                          },
                        }}
                      />
                      <TextField
                        name="interest"
                        label="What interests you about Aithon?"
                        placeholder="e.g. I want to differentiate my telecom practice with Perks"
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                          },
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: '1rem',
                        }}
                      >
                        Submit &amp; Book a Call
                      </Button>
                      <Typography
                        sx={{
                          color: TEXT_DIM,
                          fontSize: '0.82rem',
                          textAlign: 'center',
                        }}
                      >
                        Step 1: Submit your info. Step 2: Pick a time on our
                        calendar.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  /* ── Post-submit: Calendly booking ── */
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CheckIcon
                      sx={{ fontSize: 48, color: GREEN, mb: 2 }}
                    />
                    <Typography
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        mb: 1,
                      }}
                    >
                      Got it — you&rsquo;re in.
                    </Typography>
                    <Typography
                      sx={{ color: TEXT_DIM, mb: 3, lineHeight: 1.7 }}
                    >
                      Now pick a time that works. We&rsquo;ll walk you through
                      the platform, answer your questions, and show you how
                      Perks can work for your business.
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      href="https://calendly.com/aithon/partnercall"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      Book Your Partner Call
                    </Button>
                    <Typography
                      sx={{
                        color: TEXT_DIM,
                        fontSize: '0.82rem',
                        mt: 2,
                      }}
                    >
                      Prefer email? We&rsquo;ll follow up within 48 hours either
                      way.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Section>
      </Box>
    </ThemeProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=3600, stale-while-revalidate=7200'
  );
  return { props: {} };
};
